import React from 'react';
import { 
  Calendar, 
  Percent, 
  Clock, 
  PiggyBank, 
  TrendingDown, 
  CreditCard, 
  AlertCircle, 
  ArrowRight 
} from 'lucide-react';
import ToolResult, { ResultItem } from '../../../../../components/ui/ToolResult';
import { formatNumber, formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент отображения результатов расчета досрочного погашения кредита
 */
const EarlyRepaymentResults = ({ results }) => {
  if (!results) return null;

  // Форматирование даты
  const formatDate = (date) => {
    if (!date) return 'Н/Д';
    const dateObj = new Date(date);
    return dateObj.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  // Склонение месяцев
  const formatMonths = (months) => {
    const lastDigit = months % 10;
    const lastTwoDigits = months % 100;
    
    if (lastTwoDigits >= 11 && lastTwoDigits <= 19) return `${months} месяцев`;
    
    if (lastDigit === 1) return `${months} месяц`;
    if (lastDigit >= 2 && lastDigit <= 4) return `${months} месяца`;
    
    return `${months} месяцев`;
  };

  // Получение строки частоты досрочного погашения
  const getFrequencyName = (frequency) => {
    switch (frequency) {
      case 'once': return 'Разовое погашение';
      case 'monthly': return 'Ежемесячно';
      case 'quarterly': return 'Ежеквартально';
      case 'semi_annually': return 'Раз в полгода';
      case 'annually': return 'Ежегодно';
      default: return frequency;
    }
  };

  return (
    <ToolResult title="Результаты расчета досрочного погашения">
      {/* Ключевые метрики */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-6">
        {/* Экономия на процентах */}
        <div className="rounded-lg bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30 p-5 border border-green-100 dark:border-green-800/50 shadow-sm">
          <h3 className="text-base font-medium text-green-700 dark:text-green-300 mb-1.5 flex items-center gap-1.5">
            <TrendingDown className="w-4 h-4"/>
            Экономия на процентах
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-green-800 dark:text-green-200" aria-live="polite">
              {formatCurrency(results.savedInterest)}
            </span>
          </div>
          <p className="text-xs text-green-600 dark:text-green-400 mt-1">
            {formatNumber(results.effectiveInterestSavings, 1)}% от суммы досрочного погашения
          </p>
        </div>

        {/* Сокращение срока */}
        <div className="rounded-lg bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 p-5 border border-blue-100 dark:border-blue-800/50 shadow-sm">
          <h3 className="text-base font-medium text-blue-700 dark:text-blue-300 mb-1.5 flex items-center gap-1.5">
            <Clock className="w-4 h-4"/>
            Сокращение срока
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-blue-800 dark:text-blue-200" aria-live="polite">
              {formatMonths(results.savedMonths)}
            </span>
          </div>
          <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
            {results.earlyRepaymentType === 'reduction' 
              ? 'Досрочное погашение уменьшает срок кредита' 
              : 'Даже при уменьшении платежа срок сократится'}
          </p>
        </div>

        {/* Изменение платежа */}
        <div className="rounded-lg bg-gradient-to-br from-purple-50 to-violet-50 dark:from-purple-900/30 dark:to-violet-900/30 p-5 border border-purple-100 dark:border-purple-800/50 shadow-sm">
          <h3 className="text-base font-medium text-purple-700 dark:text-purple-300 mb-1.5 flex items-center gap-1.5">
            <CreditCard className="w-4 h-4"/>
            {results.earlyRepaymentType === 'payment' ? 'Уменьшение платежа' : 'Ежемесячный платеж'}
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-purple-800 dark:text-purple-200" aria-live="polite">
              {results.earlyRepaymentType === 'payment' 
                ? `${formatCurrency(results.originalMonthlyPayment)} → ${formatCurrency(results.newMonthlyPayment)}`
                : formatCurrency(results.newMonthlyPayment)}
            </span>
          </div>
          <p className="text-xs text-purple-600 dark:text-purple-400 mt-1">
            {results.earlyRepaymentType === 'payment' 
              ? `Снижение на ${formatCurrency(results.originalMonthlyPayment - results.newMonthlyPayment)} в месяц`
              : 'Платеж остается неизменным согласно стратегии'}
          </p>
        </div>
      </div>

      {/* Детализированные результаты */}
      <div className="space-y-3 mb-6">
        <ResultItem 
          label="Исходная сумма кредита" 
          value={formatCurrency(results.loanAmount)} 
          color="indigo" 
          icon={<CreditCard className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Процентная ставка" 
          value={`${formatNumber(results.interestRate, 2)}% годовых`} 
          color="orange" 
          icon={<Percent className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Срок кредита" 
          value={`${results.loanTermMonths} мес. → ${results.newTermMonths} мес.`} 
          icon={<Clock className="w-4 h-4"/>} 
        />
        
        <ResultItem 
          label="Дата окончания кредита" 
          value={`${formatDate(results.originalLastPaymentDate)} → ${formatDate(results.newLastPaymentDate)}`} 
          color="blue"
          icon={<Calendar className="w-4 h-4"/>} 
        />

        <ResultItem 
          label="Тип досрочного погашения" 
          value={results.earlyRepaymentType === 'reduction' ? 'Уменьшение срока кредита' : 'Уменьшение ежемесячного платежа'} 
          color="purple"
          icon={<PiggyBank className="w-4 h-4"/>} 
        />
        
        <ResultItem 
          label="Периодичность" 
          value={getFrequencyName(results.earlyRepaymentFrequency)} 
          color="purple"
        />
        
        <ResultItem 
          label="Сумма досрочного погашения" 
          value={formatCurrency(results.totalEarlyRepayment)} 
          color="green" 
        />

        {/* Сравнение итоговых показателей */}
        <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 mt-2">
          <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Сравнение суммарных показателей</h4>
          
          <div className="space-y-2">
            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-600 dark:text-gray-400">Переплата по процентам</span>
              <div className="flex items-center">
                <span className="text-gray-900 dark:text-white font-medium">{formatCurrency(results.originalTotalInterest)}</span>
                <ArrowRight className="w-4 h-4 mx-2 text-gray-400" />
                <span className="text-green-600 dark:text-green-400 font-medium">{formatCurrency(results.newTotalInterest)}</span>
              </div>
            </div>
            
            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-600 dark:text-gray-400">Общая сумма выплат</span>
              <div className="flex items-center">
                <span className="text-gray-900 dark:text-white font-medium">{formatCurrency(results.originalTotalPayment)}</span>
                <ArrowRight className="w-4 h-4 mx-2 text-gray-400" />
                <span className="text-green-600 dark:text-green-400 font-medium">{formatCurrency(results.newTotalPayment)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Информация об эффективности */}
        <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 mt-2 border border-green-200 dark:border-green-800">
          <div className="flex items-start gap-3">
            <div className="mt-0.5">
              <AlertCircle className="w-5 h-5 text-green-500" />
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">Эффективность досрочного погашения</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Каждый рубль, внесенный на досрочное погашение, экономит 
                <span className="font-medium text-green-600 dark:text-green-400 mx-1">
                  {formatNumber(results.effectiveInterestSavings / 100, 2)} руб.
                </span> 
                на процентах. Это эквивалентно доходности вклада в 
                <span className="font-medium text-green-600 dark:text-green-400 mx-1">
                  {formatNumber(results.effectiveInterestSavings, 1)}%
                </span> 
                при условии, что вся полученная прибыль будет направлена на погашение этого же кредита.
              </p>
            </div>
          </div>
        </div>
      </div>
    </ToolResult>
  );
};

export default EarlyRepaymentResults;